/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Nov 20 2023
 **/

import parse from "html-react-parser";

import { Avatar, Column, Grid, Icon, Loader, Row, Typography, useModal } from "@/frameworks/designStuff/components";
import { Drawer } from "@/frameworks/designStuff/compounds";
import { Each, Maybe } from "@/frameworks/libraries/components";
import { useGridResize } from "@/frameworks/libraries/hooks";
import { Configure } from "@/frameworks/libraries/utilities";
import { _P } from "@rfice/language";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { ModalID, SidebarID } from "@/frameworks/core/commons";
import { InviteChatMember } from "@/frameworks/core/components";
import { useSidebarActions } from "@/frameworks/core/hooks";
import { useChat } from "@/frameworks/core/API";

import * as S from "../emotion";
import { useChatDataStore } from "@/modules/chat/store";

export default function SearchedPeople({
	searchText,
	people
}: {
	searchText: string;
	people: WebServices.Chat.SearchUsers.Data | undefined;
}) {
	const { targetRef, column } = useGridResize<HTMLDivElement>({ width: 300, gap: 28 });
	const set_totalChat_search_area_open = useChatDataStore((state) => state.set_totalChat_search_area_open);
	const chatRoomActions = useChatRoomActions();
	const sidebarActions = useSidebarActions();
	const chatAPI = useChat();

	const modal = useModal();
	if (!people) {
		return (
			<Drawer.Body css={{ width: "100%", height: "100%" }}>
				<Column verticalAlign="center" horizonAlign="center" css={{ width: "100%", height: "100%" }}>
					<Loader />
				</Column>
			</Drawer.Body>
		);
	}
	const handleUserClick = async (user: WebServices.Chat.SearchUsers.Data[number]) => {
		set_totalChat_search_area_open(false);
		const { data } = await chatAPI.dm.checkExist.execute({
			userIds: [...new Set([user.id, Configure.userID])]
		});

		if (data?.exists) {
			chatRoomActions.common.open({ channelID: data.roomId || "", type: "DM" });
		} else {
			// 채팅 생성 열기
			modal.show({
				id: ModalID.INVITE_CHAT_MEMBER,
				content: (
					<InviteChatMember
						chatKind="DM"
						defaultUsersToInvite={[
							{ email: user.username ?? "", name: user.name, userID: user.id, valid: true }
						]}
						onHide={() => {
							modal.hide(ModalID.INVITE_CHAT_MEMBER);
						}}
					/>
				)
			});
		}
		sidebarActions.hide(SidebarID.ChatSearch);
	};
	return (
		<Drawer.Body css={{ width: "100%", height: "100%" }}>
			<Maybe
				test={people.length > 0}
				fallback={
					<Column verticalAlign="center" horizonAlign="center" css={{ width: "100%", height: "100%" }}>
						<Icon name="ToastError24" fill="#000" size={52} />
						<Typography typoSize="Body3" typoColor="neutral_70_100" align="center">
							{_P("검색 결과가 없습니다.<br />다른 검색어를 입력해주세요.")}
						</Typography>
					</Column>
				}
			>
				<Grid
					css={{ padding: "8px 60px" }}
					column={column}
					gridGap={{ row: 28 }}
					ref={targetRef}
					repeatSize={{ column: "300px" }}
				>
					<Each
						of={people}
						render={(user) => (
							<Row
								key={user.id}
								gap={12}
								verticalAlign="center"
								css={{ padding: "6px 4px", width: "292px" }}
								onClick={() => handleUserClick(user)}
							>
								<Avatar
									kind="people_32"
									status={(() => {
										switch (user.activeStatus) {
											case "ACTIVE":
												return "Green";
											case "OFFLINE":
												return "Gray";
											case "FOCUS":
												return "Red";
											case "AWAY":
												return "Yellow";
											default:
												return undefined;
										}
									})()}
									type="image"
									src={user.profileImagePath}
									text={user.name}
								/>
								<S.EmphasisSearchText typoSize="Body3" typoColor="neutral_80_100">
									{parse(
										user.name.replace(searchText, `<span id='search-text'>${searchText}</span>`)
									)}
								</S.EmphasisSearchText>
							</Row>
						)}
					/>
				</Grid>
			</Maybe>
		</Drawer.Body>
	);
}
